import React, { createContext, useContext, useState, useEffect } from 'react';

interface Settings {
    accessibleTouchTargets: boolean;
}

interface SettingsContextType {
    settings: Settings;
    toggleAccessibleTouchTargets: () => void;
}

const SETTINGS_KEY = 'clando-settings';

const defaultSettings: Settings = {
    accessibleTouchTargets: false,
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [settings, setSettings] = useState<Settings>(() => {
        const saved = localStorage.getItem(SETTINGS_KEY);
        if (saved) {
            try {
                return { ...defaultSettings, ...JSON.parse(saved) };
            } catch (e) {
                console.error('Failed to parse settings:', e);
            }
        }
        return defaultSettings;
    });

    useEffect(() => {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
    }, [settings]);

    const toggleAccessibleTouchTargets = () => {
        setSettings((prev) => ({
            ...prev,
            accessibleTouchTargets: !prev.accessibleTouchTargets,
        }));
    };

    return (
        <SettingsContext.Provider value={{ settings, toggleAccessibleTouchTargets }}>
            {children}
        </SettingsContext.Provider>
    );
};

export const useSettings = () => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};
